import classNames from 'classnames'
import { GetServerSideProps } from 'next'
import { useEffect, useRef, useState } from 'react'
import Button from 'react-bootstrap/Button'
import BSCard from 'react-bootstrap/Card'
import Spinner from 'react-bootstrap/Spinner'
import ReactPlayer from 'react-player'

import ArticlePreview from '@/components/ArticlePreview/ArticlePreview'
import Card from '@/components/Card/Card'
import CardHover from '@/components/CardHover/CardHover'
import ContentPreview from '@/components/ContentPreview/ContentPreview'
import { ExplicitHead } from '@/components/DefaultHead/DefaultHead'
import Main from '@/components/Main/Main'
import { useAds } from '@/contexts/ads'
import requestService from '@/services/request.service'
import { ArticleType, DailyResponseType, WordpressResponseType } from '@/types/home'
import checkImageDomains from '@/utils/checkImageDomains'

import styles from './index.module.scss'

type Props = {
  enableAdhesion: boolean
  initCountry?: string
  initialDaily: DailyResponseType
  initialWordpress: WordpressResponseType
}

const HomePage = ({ enableAdhesion, initialDaily, initialWordpress }: Props) => {
  const youtubeRef = useRef<HTMLDivElement>(null)
  const [youtubeHeight, setYoutubeHeight] = useState(240)
  const [youtubeWidth, setYoutubeWidth] = useState(426)
  const { loadAds } = useAds()
  const [dailyCommander] = useState(initialDaily)
  const [articles, setArticles] = useState(initialWordpress.articles)
  const [articlesPage, setArticlesPage] = useState(0)
  const [isLoadingMoreArticles, setIsLoadingMoreArticles] = useState(false)

  useEffect(() => {
    checkImageDomains()
  }, [])

  const fetchMoreArticles = async () => {
    setIsLoadingMoreArticles(true)
    const res = await fetch(`${process.env.NEXT_PUBLIC_EDHREC_JSON_URL}/static/wordpress/articles/${articlesPage + 1}`)
    const json = await res.json()
    setArticlesPage(articlesPage + 1)
    setArticles([...articles, ...json])
    setIsLoadingMoreArticles(false)
  }

  useEffect(() => {
    if (youtubeRef.current && youtubeRef.current.clientWidth < 426) {
      setYoutubeWidth(youtubeRef.current.clientWidth)
      setYoutubeHeight((youtubeRef.current.clientWidth * 9) / 16)
    }
  }, [youtubeRef.current])

  return (
    <Main>
      <ExplicitHead
        description='EDH Recommendations and strategy content for Magic: the Gathering Commander'
        keywords='EDHREC,EDH,recommendations,Commander,MTG,Magic,staples,strategy,content'
        title='EDHREC'
      />
      {/* loadAds === true && (
        <div className={styles.youtube} ref={youtubeRef}>
          <div onClick={() => window.open('https://www.youtube.com/watch?v=T5JkCdHGL_s', '_blank')} role='button'>
            <ReactPlayer
              height={`${youtubeHeight}px`}
              muted
              playing
              style={{ pointerEvents: 'none' }}
              url='https://www.youtube.com/watch?v=T5JkCdHGL_s'
              width={`${youtubeWidth}px`}
            />
          </div>
        </div>
      ) */}
      <div className={styles.container}>
        <div className={styles.containerLeft}>
          {articles
            .slice(0, -1)
            .reduce<ArticleType[][]>((res, _, i, arr) => {
              if (i % 2 === 0) res.push(arr.slice(i, i + 2))
              return res
            }, [])
            .map((articleRow) => (
              <div
                className={classNames(styles.grid, styles.row)}
                key={articleRow.map((article) => article.link).join()}
              >
                {articleRow.map((article) => (
                  <div className={styles.rowItem} key={article.link}>
                    {article.youtube ? <ContentPreview content={article} /> : <ArticlePreview article={article} />}
                  </div>
                ))}
              </div>
            ))}
          <div className={styles.row}>
            <span className={styles.loadMore}>
              <Button onClick={fetchMoreArticles} variant='link'>
                {isLoadingMoreArticles ? <Spinner animation='border' /> : 'Load More Articles'}
              </Button>
            </span>
          </div>
        </div>
        <div className={styles.containerRight}>
          <div className={styles.row}>
            <div className={styles.rowItem}>
              <div className={styles.rowContent}>
                <h5 className='title pb-2'>Commander of the Day</h5>
                <div className={styles.dailyCommander}>
                  <Card card={dailyCommander.daily} />
                </div>
              </div>
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.rowItem}>
              <h5 className='title'>Top Commanders Weekly</h5>
              <BSCard className={classNames(styles.rowContent, 'shadow-sm')}>
                <BSCard.Body>
                  <ul>
                    {dailyCommander.weekly.map(({ name, url }, i) => (
                      <li key={i}>
                        {i + 1}. <CardHover left name={name} url={url} />
                      </li>
                    ))}
                  </ul>
                </BSCard.Body>
              </BSCard>
            </div>
          </div>
          {dailyCommander?.trending && (
            <div className={styles.row}>
              <div className={styles.rowItem}>
                <h5 className='title'>Trending Cards</h5>
                <BSCard className={classNames(styles.rowContent, 'shadow-sm')}>
                  <BSCard.Body>
                    <ul>
                      {dailyCommander.trending.map(({ name, url }, i) => (
                        <li key={i}>
                          {i + 1}. <CardHover left name={name} url={url} />
                        </li>
                      ))}
                    </ul>
                  </BSCard.Body>
                </BSCard>
              </div>
            </div>
          )}
        </div>
      </div>
      {enableAdhesion && <div className='mvHomeAdhesion' />}
    </Main>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ req }) => {
  try {
    const daily = await (await requestService.get(`${process.env.NEXT_PUBLIC_EDHREC_JSON_URL}/static/daily`)).json()
    const wordpress = await (
      await requestService.get(`${process.env.NEXT_PUBLIC_EDHREC_JSON_URL}/static/wordpress`.replace('beta', ''))
    ).json()
    const props: Props = {
      enableAdhesion: true,
      initialDaily: daily,
      initialWordpress: wordpress,
    }
    if (req.headers['cloudfront-viewer-country']) props.initCountry = req.headers['cloudfront-viewer-country'] as string
    return { props }
  } catch {
    throw new Error('Failed to fetch data')
  }
}

export default HomePage
